import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import './PortalPagos.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4003/api/v1'

function EstadoPago() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [pago, setPago] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [verificando, setVerificando] = useState(false)

  // Obtener referencia del URL o de sessionStorage
  const referencia = searchParams.get('id') || sessionStorage.getItem('pago_referencia')
  const comercioNombre = sessionStorage.getItem('pago_comercio')

  useEffect(() => {
    if (referencia) {
      consultarEstado()
    } else {
      setError('No se encontró la referencia del pago')
      setLoading(false)
    }
  }, [referencia])

  const consultarEstado = async () => {
    try {
      const response = await fetch(`${API_URL}/pagos/estado/${referencia}`)
      const data = await response.json()

      if (data.success) {
        setPago(data.pago)

        // Si está pendiente, seguir verificando
        if (data.pago.estado === 'PENDIENTE' || data.pago.estado === 'PROCESANDO') {
          setTimeout(consultarEstado, 3000) // Verificar cada 3 segundos
        }
      } else {
        setError(data.message || 'No se pudo consultar el estado del pago')
      }
    } catch (err) {
      setError('Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  const verificarManualmente = async () => {
    setVerificando(true)
    await consultarEstado()
    setVerificando(false)
  }

  const getEstadoInfo = () => {
    if (!pago) return null

    switch (pago.estado) {
      case 'APROBADO':
        return {
          icono: '✓',
          titulo: '¡Pago exitoso!',
          clase: 'estado-aprobado',
          mensaje: 'Tu pago ha sido procesado correctamente y tu licencia ha sido renovada.',
          mostrarDetalles: true
        }
      case 'PENDIENTE':
      case 'PROCESANDO':
        return {
          icono: '⏳',
          titulo: 'Pago en proceso',
          clase: 'estado-pendiente',
          mensaje: 'Tu pago está siendo procesado. Esto puede tomar unos minutos.',
          mostrarDetalles: false
        }
      case 'RECHAZADO':
        return {
          icono: '✗',
          titulo: 'Pago rechazado',
          clase: 'estado-rechazado',
          mensaje: 'Lo sentimos, tu pago no pudo ser procesado. Por favor intenta nuevamente con otro método de pago.',
          mostrarDetalles: false
        }
      case 'ANULADO':
        return {
          icono: '⊘',
          titulo: 'Pago anulado',
          clase: 'estado-anulado',
          mensaje: 'Este pago ha sido anulado.',
          mostrarDetalles: false
        }
      case 'ERROR':
        return {
          icono: '!',
          titulo: 'Error en el pago',
          clase: 'estado-error',
          mensaje: 'Ocurrió un error al procesar tu pago. Por favor intenta nuevamente.',
          mostrarDetalles: false
        }
      default:
        return {
          icono: '?',
          titulo: 'Estado desconocido',
          clase: 'estado-desconocido',
          mensaje: 'No pudimos determinar el estado de tu pago.',
          mostrarDetalles: false
        }
    }
  }

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A'
    return new Date(fecha).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const volverInicio = () => {
    sessionStorage.removeItem('pago_referencia')
    sessionStorage.removeItem('pago_comercio')
    navigate('/pagar')
  }

  if (loading) {
    return (
      <div className="portal-pagos">
        <header className="portal-header">
          <div className="header-content">
            <h1>Posfac</h1>
            <p>Portal de Pagos</p>
          </div>
        </header>
        <main className="portal-main">
          <div className="portal-container">
            <div className="loading-estado">
              <div className="spinner-large"></div>
              <p>Consultando estado del pago...</p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div className="portal-pagos">
        <header className="portal-header">
          <div className="header-content">
            <h1>Posfac</h1>
            <p>Portal de Pagos</p>
          </div>
        </header>
        <main className="portal-main">
          <div className="portal-container">
            <div className="estado-error-container">
              <div className="estado-icono estado-error">!</div>
              <h2>Error</h2>
              <p>{error}</p>
              <button className="btn-volver-inicio" onClick={volverInicio}>
                Volver al inicio
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  const estadoInfo = getEstadoInfo()

  return (
    <div className="portal-pagos">
      <header className="portal-header">
        <div className="header-content">
          <h1>Posfac</h1>
          <p>Portal de Pagos</p>
        </div>
      </header>

      <main className="portal-main">
        <div className="portal-container">
          <div className="estado-pago-container">

            {/* Icono del estado */}
            <div className={`estado-icono ${estadoInfo.clase}`}>
              {estadoInfo.icono}
            </div>

            {/* Título y mensaje */}
            <h2>{estadoInfo.titulo}</h2>
            <p className="estado-mensaje">{estadoInfo.mensaje}</p>

            {/* Detalles del pago */}
            <div className="detalles-pago">
              <div className="detalle-item">
                <span className="label">Referencia:</span>
                <span className="value">{pago.referencia}</span>
              </div>

              <div className="detalle-item">
                <span className="label">Comercio:</span>
                <span className="value">{pago.comercio || comercioNombre}</span>
              </div>

              <div className="detalle-item">
                <span className="label">Plan:</span>
                <span className="value">{pago.plan}</span>
              </div>

              <div className="detalle-item">
                <span className="label">Monto:</span>
                <span className="value monto">{pago.monto_total_formato}</span>
              </div>

              {pago.metodo_pago && (
                <div className="detalle-item">
                  <span className="label">Método de pago:</span>
                  <span className="value">
                    {pago.metodo_pago}
                    {pago.tarjeta_ultimos_4 && ` •••• ${pago.tarjeta_ultimos_4}`}
                    {pago.tarjeta_tipo && ` (${pago.tarjeta_tipo})`}
                  </span>
                </div>
              )}

              {pago.fecha_pago && (
                <div className="detalle-item">
                  <span className="label">Fecha de pago:</span>
                  <span className="value">{formatearFecha(pago.fecha_pago)}</span>
                </div>
              )}

              {pago.licencia_renovada && (
                <div className="detalle-item licencia-renovada">
                  <span className="badge badge-success">✓ Licencia renovada automáticamente</span>
                </div>
              )}
            </div>

            {/* Botón para verificar manualmente */}
            {(pago.estado === 'PENDIENTE' || pago.estado === 'PROCESANDO') && (
              <button
                className="btn-verificar"
                onClick={verificarManualmente}
                disabled={verificando}
              >
                {verificando ? 'Verificando...' : 'Verificar estado'}
              </button>
            )}

            {/* Botones de acción */}
            <div className="acciones-estado">
              {pago.estado === 'RECHAZADO' || pago.estado === 'ERROR' ? (
                <button className="btn-reintentar" onClick={volverInicio}>
                  Intentar nuevamente
                </button>
              ) : (
                <button className="btn-volver-inicio" onClick={volverInicio}>
                  Volver al inicio
                </button>
              )}
            </div>

            {/* Información adicional para pagos exitosos */}
            {pago.estado === 'APROBADO' && (
              <div className="info-adicional">
                <p>
                  Hemos enviado un correo electrónico con los detalles de tu pago.
                  Tu licencia ya está activa y podrás continuar usando el sistema normalmente.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="portal-footer">
        <p>Posfac © {new Date().getFullYear()} - Sistema POS</p>
        <p>Pagos procesados de forma segura por Wompi</p>
      </footer>
    </div>
  )
}

export default EstadoPago
